import * as React from 'react';
import {
  Modal,
  ModalOverlay,
  Dialog,
  SearchField,
  Input,
  ListBox,
  ListBoxItem,
  type Key,
} from 'react-aria-components';
import { Search2Line, CloseLine } from '@mingcute/react';

import { cn } from '../lib/cn';
import { useKeyboardShortcut } from '../hooks/use-keyboard-shortcut';
import { useLocalStorage } from '../hooks/use-local-storage';

/**
 * Klaret Command Palette (handoff §10.7).
 * ⌘K / Ctrl+K; --tvl-e3, --tvl-r4, 560 px, top 20%.
 * 44 px borderless search with leading magnifier.
 * Results grouped by module with footnote group labels.
 * Matched substrings body-medium (never yellow highlight).
 * RAC Modal + Dialog + SearchField + ListBox — no cmdk, no Radix Dialog.
 */

export interface CommandItem {
  id: string;
  label: React.ReactNode;
  /** Search keywords used for filtering. */
  keywords?: string[];
  shortcut?: string;
  icon?: React.ReactNode;
  group?: string;
  run: () => void;
}

export interface CommandPaletteProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  commands: CommandItem[];
  /** Toggle on Cmd/Ctrl+K. Default: true. */
  enableShortcut?: boolean;
  placeholder?: string;
  emptyMessage?: React.ReactNode;
  /** localStorage key for recent commands. */
  recentsKey?: string;
  recentsLimit?: number;
  /** Fired as the user types — use to drive async record search. */
  onInputValueChange?: (value: string) => void;
  /** Disable built-in client filtering (e.g. when results are server-filtered). Default true. */
  filter?: boolean;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  open: openProp,
  onOpenChange,
  commands,
  enableShortcut = true,
  placeholder = 'Type a command or search…',
  emptyMessage = 'No results found.',
  recentsKey = 'teivor.cmdk.recents',
  recentsLimit = 5,
  onInputValueChange,
  filter = true,
}) => {
  const isControlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(false);
  const open = isControlled ? openProp! : internalOpen;
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  useKeyboardShortcut(
    'cmd+k',
    (e) => {
      e.preventDefault();
      setOpen(!open);
    },
    { enabled: enableShortcut },
  );

  const [query, setQuery] = React.useState('');

  // Reset query when closed
  React.useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const [recentIds, setRecentIds] = useLocalStorage<string[]>(recentsKey, []);

  const cmdById = React.useMemo(() => {
    const m = new Map<string, CommandItem>();
    commands.forEach((c) => m.set(c.id, c));
    return m;
  }, [commands]);

  const recentCommands = React.useMemo(
    () => recentIds.map((id) => cmdById.get(id)).filter(Boolean) as CommandItem[],
    [recentIds, cmdById],
  );

  const showRecents = recentCommands.length > 0 && !query.trim();
  const recentIdSet = React.useMemo(() => new Set(recentIds), [recentIds]);

  // Drop one entry from the recents history. The command then reappears in its
  // normal group (e.g. Navigate): a command lives in EITHER Recent or a group,
  // never both.
  const removeRecent = React.useCallback(
    (id: string) => setRecentIds((prev) => prev.filter((x) => x !== id)),
    [setRecentIds],
  );

  // Client-side filtering
  const filteredCommands = React.useMemo(() => {
    if (!filter || !query.trim()) return commands;
    const q = query.toLowerCase();
    return commands.filter((c) => {
      const label = typeof c.label === 'string' ? c.label.toLowerCase() : '';
      const kw = (c.keywords ?? []).join(' ').toLowerCase();
      return label.includes(q) || kw.includes(q) || c.id.toLowerCase().includes(q);
    });
  }, [commands, query, filter]);

  const groups = React.useMemo(() => {
    const map = new Map<string, CommandItem[]>();
    for (const c of filteredCommands) {
      // Mutual exclusivity: while Recent is shown, a command that is in recents is
      // omitted from its group so it never appears in both places.
      if (showRecents && recentIdSet.has(c.id)) continue;
      const g = c.group ?? 'Commands';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(c);
    }
    return Array.from(map.entries());
  }, [filteredCommands, showRecents, recentIdSet]);

  const runCommand = React.useCallback(
    (cmd: CommandItem) => {
      setRecentIds((prev) => {
        const next = [cmd.id, ...prev.filter((id) => id !== cmd.id)].slice(0, recentsLimit);
        return next;
      });
      setOpen(false);
      requestAnimationFrame(() => cmd.run());
    },
    [setRecentIds, recentsLimit, setOpen],
  );

  const handleAction = React.useCallback(
    (key: Key) => {
      const id = String(key);
      // strip "recent-" prefix if present
      const realId = id.startsWith('recent-') ? id.slice(7) : id;
      const cmd = cmdById.get(realId);
      if (cmd) runCommand(cmd);
    },
    [cmdById, runCommand],
  );

  // Build flat item list for ListBox — sections rendered via group headers
  // We render one ListBox per group for visual grouping with footnote labels.

  return (
    <ModalOverlay
      isOpen={open}
      onOpenChange={setOpen}
      isDismissable
      className={cn(
        'fixed inset-0 flex items-start justify-center',
        // Dark dimming scrim — NOT --tvl-ink (near-white in dark mode → lightened the page).
        'bg-[var(--tvl-scrim)]',
        'z-[var(--tvl-z-command)]',
        'pt-[20vh]',
        'entering:animate-in entering:fade-in-0',
        'exiting:animate-out exiting:fade-out-0',
      )}
    >
      <Modal
        className={cn(
          'w-[min(560px,92vw)]',
          'rounded-[var(--tvl-r4)] bg-[var(--tvl-raised)] shadow-[var(--tvl-e3)]',
          'overflow-hidden',
          'entering:animate-in entering:fade-in-0 entering:slide-in-from-top-2',
          'exiting:animate-out exiting:fade-out-0 exiting:slide-out-to-top-2',
        )}
      >
        <Dialog aria-label="Command palette" className="outline-none flex flex-col">
          {/* Search row — 44 px, borderless */}
          <SearchField
            value={query}
            onChange={(v) => {
              setQuery(v);
              onInputValueChange?.(v);
            }}
            aria-label="Search commands"
            className="flex items-center gap-2 border-b border-[var(--tvl-hairline)] px-3"
          >
            <Search2Line size={16} color="var(--tvl-ink-3)" aria-hidden />
            <Input
              placeholder={placeholder}
              className={cn(
                'h-11 flex-1 bg-transparent',
                'text-[14px] leading-[20px] text-[var(--tvl-ink)]',
                'placeholder:text-[var(--tvl-ink-3)]',
                'outline-none',
              )}
            />
          </SearchField>

          {/* Results */}
          <div className="max-h-[60vh] overflow-y-auto p-2">
            {showRecents ? (
              <div>
                <div className="px-2 py-1.5 text-[11px] font-medium uppercase tracking-wider text-[var(--tvl-ink-3)]">
                  Recent
                </div>
                <ListBox
                  aria-label="Recent commands"
                  selectionMode="none"
                  onAction={handleAction}
                  className="outline-none"
                >
                  {recentCommands.map((cmd) => (
                    <CommandRow
                      key={`recent-${cmd.id}`}
                      id={`recent-${cmd.id}`}
                      cmd={cmd}
                      onRemove={() => removeRecent(cmd.id)}
                    />
                  ))}
                </ListBox>
              </div>
            ) : null}

            {groups.length === 0 ? (
              <div className="py-8 text-center text-[14px] leading-[20px] text-[var(--tvl-ink-3)]">
                {emptyMessage}
              </div>
            ) : (
              groups.map(([groupName, items]) => (
                <div key={groupName}>
                  <div className="px-2 py-1.5 text-[11px] font-medium uppercase tracking-wider text-[var(--tvl-ink-3)]">
                    {groupName}
                  </div>
                  <ListBox
                    aria-label={groupName}
                    selectionMode="none"
                    onAction={handleAction}
                    className="outline-none"
                  >
                    {items.map((cmd) => (
                      <CommandRow key={cmd.id} id={cmd.id} cmd={cmd} />
                    ))}
                  </ListBox>
                </div>
              ))
            )}
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};
CommandPalette.displayName = 'CommandPalette';

// Internal row — not exported (per compound API: only CommandPalette + CommandItem exported)
const CommandRow: React.FC<{ id: string; cmd: CommandItem; onRemove?: () => void }> = ({
  id,
  cmd,
  onRemove,
}) => (
  <ListBoxItem
    id={id}
    textValue={typeof cmd.label === 'string' ? cmd.label : cmd.id}
    className={cn(
      'group flex cursor-pointer items-center gap-2 rounded-[var(--tvl-r2)] px-2 py-2',
      'text-[14px] leading-[20px] text-[var(--tvl-ink)]',
      'outline-none',
      'hover:bg-[var(--tvl-wash-hover)] focus:bg-[var(--tvl-wash-hover)]',
      'pressed:bg-[var(--tvl-wash-pressed)]',
      'selected:bg-[var(--tvl-selected)]',
    )}
  >
    {cmd.icon ? (
      <span className="inline-flex h-4 w-4 items-center justify-center text-[var(--tvl-ink-2)]">
        {cmd.icon}
      </span>
    ) : null}
    <span className="flex-1 truncate">{cmd.label}</span>
    {cmd.shortcut ? (
      <kbd
        className={cn(
          'ml-2 hidden rounded-[var(--tvl-r1)] border border-[var(--tvl-hairline)]',
          'bg-[var(--tvl-sunken)] px-1.5 py-0.5',
          'font-[var(--tvl-font-mono)] text-[11px] text-[var(--tvl-ink-3)]',
          'sm:inline-block',
        )}
      >
        {cmd.shortcut}
      </kbd>
    ) : null}
    {onRemove ? (
      // Remove this entry from recents. stopPropagation on pointer-down keeps the
      // row's onAction (navigate) from firing when the X is clicked.
      <button
        type="button"
        aria-label="Remove from recent"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className={cn(
          'ml-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-[var(--tvl-r1)]',
          'text-[var(--tvl-ink-3)] hover:bg-[var(--tvl-hairline)] hover:text-[var(--tvl-ink)]',
          'outline-none focus-visible:bg-[var(--tvl-hairline)] focus-visible:text-[var(--tvl-ink)]',
        )}
      >
        <CloseLine size={14} aria-hidden />
      </button>
    ) : null}
  </ListBoxItem>
);
